// Study-type classifier (pattern layer).
//
// Keyword scoring across title/abstract/methods. Title-weight 4x, abstract 2x,
// methods 1x. Returns top-1 with confidence and top-2 alternatives.
//
// Per PRD §6.8: when the manuscript looks like a narrative review / perspective /
// editorial, the classifier flags `noFormalGuideline: true` so the UI says so
// plainly rather than force-fitting an EQUATOR checklist.

import type { ManuscriptDocument } from '../pdf/types';
import { sectionAt } from '../pdf/sections';
import type { ClassifierAlternative, ClassifierLabel, ClassifierResult } from './types';

interface LabelRule {
  label: ClassifierLabel;
  /** Phrases matched case-insensitively against title/abstract/methods. */
  cues: string[];
  /** Negative cues that subtract from the score if present anywhere. */
  antiCues?: string[];
  /** True if this label has no formal EQUATOR guideline. */
  noFormalGuideline?: boolean;
}

const RULES: LabelRule[] = [
  {
    label: 'randomised controlled trial',
    cues: [
      'randomised controlled trial',
      'randomized controlled trial',
      'randomised trial',
      'randomized trial',
      'randomly assigned',
      'randomly allocated',
      'placebo-controlled',
      'double-blind',
      'parallel-group',
      'rct',
      'allocation concealment',
      'block randomisation',
      'block randomization',
    ],
  },
  {
    label: 'systematic review',
    cues: [
      'systematic review',
      'meta-analysis',
      'meta analysis',
      'prisma',
      'scoping review',
      'we searched',
      'records identified',
      'medline',
      'embase',
      'cochrane',
      'forest plot',
    ],
  },
  {
    label: 'cohort study',
    cues: [
      'cohort study',
      'cohort',
      'prospective cohort',
      'retrospective cohort',
      'person-year',
      'person-years',
      'incidence',
      'followed up',
      'follow-up of',
    ],
    antiCues: ['systematic review', 'randomised', 'randomized'],
  },
  {
    label: 'case-control study',
    cues: [
      'case-control',
      'case control',
      'matched controls',
      'case patients',
      'odds ratio',
    ],
  },
  {
    label: 'cross-sectional study',
    cues: ['cross-sectional', 'cross sectional', 'prevalence', 'point prevalence'],
  },
  {
    label: 'diagnostic accuracy study',
    cues: [
      'diagnostic accuracy',
      'sensitivity and specificity',
      'reference standard',
      'index test',
      'roc curve',
      'auc of',
      'predictive value',
      'likelihood ratio',
    ],
    // A *systematic review / meta-analysis* of diagnostic accuracy (PRISMA /
    // PRISMA-DTA) is full of these cues but must NOT route to STARD — STARD
    // is for primary diagnostic-accuracy studies. Suppress when review signals
    // are present so it routes to PRISMA instead.
    antiCues: ['systematic review', 'meta-analysis', 'meta analysis', 'prisma'],
  },
  {
    label: 'case report',
    cues: ['case report', 'we report a case', 'we present a case', 'case presentation'],
    antiCues: ['case-control', 'case series', 'systematic review'],
  },
  {
    label: 'narrative review',
    cues: ['narrative review', 'this review', 'an overview of', 'in this review'],
    antiCues: ['systematic review', 'meta-analysis'],
    noFormalGuideline: true,
  },
  {
    label: 'editorial',
    cues: ['editorial', "editor's view"],
    noFormalGuideline: true,
  },
  {
    label: 'perspective',
    cues: ['perspective', 'viewpoint', 'opinion piece'],
    noFormalGuideline: true,
  },
];

const TITLE_WEIGHT = 4;
const ABSTRACT_WEIGHT = 2;
const METHODS_WEIGHT = 1;

function getSectionText(doc: ManuscriptDocument, section: 'abstract' | 'methods'): string {
  const span = doc.sections.find((s) => s.section === section);
  if (!span) return '';
  return doc.rawText.slice(span.start, span.end).toLowerCase();
}

function countPhrase(haystack: string, needle: string): number {
  if (!needle || !haystack) return 0;
  const n = needle.toLowerCase();
  let count = 0;
  let idx = haystack.indexOf(n);
  while (idx >= 0) {
    count++;
    idx = haystack.indexOf(n, idx + n.length);
  }
  return count;
}

export function classifyStudyType(doc: ManuscriptDocument): ClassifierResult {
  const title = doc.title.toLowerCase();
  const abstract = getSectionText(doc, 'abstract');
  const methods = getSectionText(doc, 'methods');
  const fullLower = doc.rawText.toLowerCase();

  const scores = new Map<ClassifierLabel, number>();
  for (const rule of RULES) {
    let score = 0;
    for (const cue of rule.cues) {
      score += countPhrase(title, cue) * TITLE_WEIGHT;
      score += countPhrase(abstract, cue) * ABSTRACT_WEIGHT;
      score += countPhrase(methods, cue) * METHODS_WEIGHT;
    }
    for (const anti of rule.antiCues ?? []) {
      if (countPhrase(fullLower, anti) > 0) score = Math.max(0, score - TITLE_WEIGHT);
    }
    scores.set(rule.label, score);
  }

  // The Editorial Manager cover's "Article Type" is an authoritative signal
  // the body text often understates. A "Review Article" with systematic /
  // meta-analysis / PRISMA cues is a systematic review (→ PRISMA); without
  // them it's a narrative review (no formal EQUATOR guideline).
  const at = (doc.articleType ?? '').toLowerCase();
  if (/review/.test(at)) {
    const sysCues =
      countPhrase(fullLower, 'systematic review') +
      countPhrase(fullLower, 'meta-analysis') +
      countPhrase(fullLower, 'meta analysis') +
      countPhrase(fullLower, 'prisma');
    const boostLabel: ClassifierLabel =
      sysCues > 0 ? 'systematic review' : 'narrative review';
    scores.set(boostLabel, (scores.get(boostLabel) ?? 0) + 2 * TITLE_WEIGHT);
  } else if (/case report/.test(at)) {
    scores.set('case report', (scores.get('case report') ?? 0) + 2 * TITLE_WEIGHT);
  }

  const ranked = [...scores.entries()]
    .map<ClassifierAlternative>(([label, score]) => ({ label, score }))
    .sort((a, b) => b.score - a.score);

  const top = ranked[0];
  const second = ranked[1];

  if (!top || top.score === 0) {
    return {
      label: 'unclassified',
      confidence: 0,
      alternatives: ranked.slice(0, 2).filter((r) => r.score > 0),
      noFormalGuideline: false,
      rationale:
        'No study-type cues matched in title, abstract, or methods. Manuscript may need manual classification.',
    };
  }

  // Calibrated confidence = margin × evidence:
  //   margin   = top / (top+second)  — separation of the winner (0.5..1)
  //   evidence = 1 - e^(-top/6)      — absolute amount of signal (saturating)
  // A single title cue (score 4, nothing else) → ~0.49 (prompts "please
  // confirm") instead of the old 0.99 over-confidence; a strong, well-
  // separated match still lands high. Capped at 0.97 — never claim certainty.
  const denom = top.score + (second?.score ?? 0);
  const margin = denom === 0 ? 0 : top.score / denom;
  const evidence = 1 - Math.exp(-top.score / 6);
  const confidence = Math.max(0, Math.min(0.97, margin * evidence));

  const topRule = RULES.find((r) => r.label === top.label)!;
  return {
    label: top.label,
    confidence,
    alternatives: ranked.slice(1, 3).filter((r) => r.score > 0),
    noFormalGuideline: Boolean(topRule.noFormalGuideline),
    rationale: buildRationale(top, second, topRule),
  };
}

function buildRationale(
  top: ClassifierAlternative,
  second: ClassifierAlternative | undefined,
  topRule: LabelRule,
): string {
  const parts: string[] = [];
  parts.push(`Top match: "${top.label}" (score ${top.score}).`);
  if (second && second.score > 0) {
    parts.push(`Closest alternative: "${second.label}" (score ${second.score}).`);
  }
  if (topRule.noFormalGuideline) {
    parts.push(
      'This study type has no formal EQUATOR guideline; assess against ICMJE narrative norms.',
    );
  }
  return parts.join(' ');
}

// Re-export sectionAt indirectly for downstream callers that need section detection.
export { sectionAt };
